import { PermissionEnum } from '@Application/Security/Roles';
import ClearTemplateAssignment from '@Application/Documents/TemplateAssignments/Commands/ClearTemplateAssignment';
import SaveTemplateAssignment from '@Application/Documents/TemplateAssignments/Commands/SaveTemplateAssignment';
import type { TemplateAssignmentScope } from '@Application/Documents/TemplateAssignments/TemplateAssignmentScope';
import clearTemplateAssignment from '@Application/Documents/TemplateAssignments/clearTemplateAssignment';
import saveTemplateAssignment from '@Application/Documents/TemplateAssignments/saveTemplateAssignment';
import { fieldWithoutMarginStyles } from '@Guidelines/Theme/commonPassthrough';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import type DocumentTemplatesListView from '@UI/Store/DocumentTemplates/Views/Document/DocumentTemplatesListView';
import type DocumentTypesListView from '@UI/Store/DocumentTypes/Views/DocumentTypesListView';
import type TemplateAssignmentView from '@UI/Store/DocumentTemplates/Views/TemplateAssignements/TemplateAssignmentView';
import { css } from '@emotion/react';
import { useDispatch } from '@verclam/synapse';
import { Button, ConfirmDialog, Dropdown } from '@verclam/ui-kit';
import { X } from 'lucide-react';
import * as React from 'react';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

const actionsRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.25rem;
	flex-wrap: nowrap;
`;

const dropdownStyle = css`
	flex: 1;
`;

/** "Retirer" clears the type's own organization assignment; "Retirer la surcharge" clears only the
 * facility's own override, never the organization's assignment underneath it. */
const REMOVE_LABEL_BY_SCOPE: Record<TemplateAssignmentScope['type'], string> = {
	organization: 'Retirer',
	facility: 'Retirer la surcharge',
};

const REMOVE_HEADER_BY_SCOPE: Record<TemplateAssignmentScope['type'], string> = {
	organization: 'Retirer l’affectation',
	facility: 'Retirer la surcharge',
};

const DROPDOWN_PLACEHOLDER_BY_SCOPE: Record<TemplateAssignmentScope['type'], string> = {
	organization: 'Choisir un gabarit',
	facility: 'Surcharger avec…',
};

type Props = {
	readonly assignment: TemplateAssignmentView;
	readonly scope: TemplateAssignmentScope;
	readonly templates: DocumentTemplatesListView;
	/** Resolves the entry's document type label — the type itself is only an identifier on the wire. */
	readonly documentTypes: DocumentTypesListView;
};

/**
 * Leaf actions: dispatches itself, never a callback prop — same pattern as `UsageAssignmentCardActions`.
 * The dropdown only ever offers gabarits of this entry's own document type, never archived (a template
 * `Dropdown` from `@verclam/ui-kit`). Retiring always targets this card's own `scope`: at the
 * organization level that is the type's assignment, at the facility level only the facility's own
 * override — never the inherited gabarit below it, which this action cannot see or touch.
 */
const TemplateAssignmentCardActions: React.FC<Props> = ({ assignment, scope, templates, documentTypes }) => {
	const dispatch = useDispatch();
	const [isRemoveConfirmVisible, setRemoveConfirmVisible] = React.useState<boolean>(false);

	const documentTypeLabel = React.useMemo(
		() => documentTypes.labelOf(assignment.documentTypeId),
		[documentTypes, assignment.documentTypeId],
	);

	const availableTemplates = React.useMemo(
		() =>
			templates.list.filter(
				(template) => !template.isArchived && template.documentTypeId === assignment.documentTypeId,
			),
		[templates, assignment.documentTypeId],
	);

	const handleTemplateChange = React.useCallback(
		(templateId: number | null) => {
			if (templateId === null) {
				setRemoveConfirmVisible(true);
				return;
			}

			dispatch(
				saveTemplateAssignment(
					new SaveTemplateAssignment(scope, assignment.documentTypeId, templateId),
				),
			).catch(ignoreHandledError);
		},
		[dispatch, scope, assignment.documentTypeId],
	);

	const handleRemoveClick = React.useCallback(() => {
		setRemoveConfirmVisible(true);
	}, []);

	const handleRemoveHide = React.useCallback(() => {
		setRemoveConfirmVisible(false);
	}, []);

	const handleRemoveAccept = React.useCallback(() => {
		dispatch(
			clearTemplateAssignment(new ClearTemplateAssignment(scope, assignment.documentTypeId)),
		).catch(ignoreHandledError);
		setRemoveConfirmVisible(false);
	}, [dispatch, scope, assignment.documentTypeId]);

	return (
		<div css={actionsRowStyle}>
			<WithPermission permission={PermissionEnum.DOCUMENT_TEMPLATE_WRITE}>
				<div css={dropdownStyle}>
					<Dropdown<number>
						name={`template-assignment-${scope.type}-${assignment.documentTypeId}`}
						label={`Gabarit pour ${documentTypeLabel}`}
						isLabelHidden
						options={availableTemplates}
						optionLabel="name"
						optionValue="id"
						value={assignment.templateId}
						placeholder={DROPDOWN_PLACEHOLDER_BY_SCOPE[scope.type]}
						onChange={handleTemplateChange}
						pt={fieldWithoutMarginStyles}
						isFormFieldValid={scope.type !== 'organization' || assignment.templateId !== null}
						errorMessage={
							scope.type === 'organization' &&
							`Aucun gabarit n’est affecté aux documents « ${documentTypeLabel} ».`
						}
					/>
				</div>
			</WithPermission>

			<ConditionalLoader data={assignment.templateId}>
				<WithPermission permission={PermissionEnum.DOCUMENT_TEMPLATE_WRITE}>
					<Button
						type="button"
						severity="danger"
						css={{ marginTop: '0.5rem', alignSelf: 'flex-start' }}
						outlined
						tooltip={REMOVE_LABEL_BY_SCOPE[scope.type]}
						tooltipOptions={{ position: 'top' }}
						icon={<X size={16} />}
						onClick={handleRemoveClick}
					/>
					<ConfirmDialog
						blockScroll
						closable
						draggable={false}
						visible={isRemoveConfirmVisible}
						onHide={handleRemoveHide}
						header={REMOVE_HEADER_BY_SCOPE[scope.type]}
						message={
							scope.type === 'organization' ? (
								<p>
									Retirer le gabarit affecté aux documents « {documentTypeLabel} » ?
									<br />
									Les agences sans surcharge ne pourront plus télécharger ces documents tant
									qu’aucun autre n’est affecté.
								</p>
							) : (
								<p>
									Retirer la surcharge ?
									<br />
									L’agence utilisera le gabarit de la structure.
								</p>
							)
						}
						accept={handleRemoveAccept}
						reject={handleRemoveHide}
					/>
				</WithPermission>
			</ConditionalLoader>
		</div>
	);
};

export default TemplateAssignmentCardActions;
